import React, { useState, useMemo } from 'react';

type TabProps = {
  title: string;
  children: React.ReactNode;
  badge?: string | number;
  // accent — красный пилюлька (по умолчанию). Используется для
  // нотификаций / непрочитанных запросов: «Запросы на присоединение».
  // neutral — мягкий серый, для информационных счётчиков:
  // «Активные / На модерации / …».
  badgeTone?: 'accent' | 'neutral';
};

type TabsProps = {
  children: React.ReactElement<TabProps>[] | React.ReactElement<TabProps>;
  initialActiveTab?: number;
  variant?: 'default' | 'underline' | 'booking';
  className?: string;
  contentAfterTabs?: () => React.ReactNode;
};

const Tab: React.FC<TabProps> = ({ children }) => {
  return <>{children}</>;
};

const Tabs: React.FC<TabsProps> = ({
  children,
  initialActiveTab = 0,
  variant = 'default',
  className = '',
  contentAfterTabs = null,
}) => {
  const [activeTab, setActiveTab] = useState<number>(initialActiveTab);

  const filteredChildren = useMemo(
    () => React.Children.toArray(children).filter((child: any) => child.props.title),
    [children]
  );

  const getTabClasses = (index: number) => {
    const isActive = activeTab === index;

    if (variant === 'booking') {
      return isActive
        ? 'px-6 py-2.5 cursor-pointer text-sm font-semibold text-[#2d2c2c] border border-[#2d2c2c]/20 border-b-white rounded-t-lg -mb-px bg-white z-10 relative'
        : 'px-6 py-2.5 cursor-pointer text-sm text-[#2d2c2c]/50 border border-transparent border-b-[#2d2c2c]/20 rounded-t-lg hover:text-[#2d2c2c]/70 transition-colors';
    }

    if (variant === 'underline') {
      return isActive
        ? 'py-2.5 px-1 cursor-pointer text-sm font-semibold text-[#2d2c2c] border-b-2 border-[#e3282a]'
        : 'py-2.5 px-1 cursor-pointer text-sm text-gray-400 border-b-2 border-transparent hover:text-gray-600 transition-colors';
    }

    return isActive
      ? 'py-2.5 px-3 cursor-pointer text-sm font-bold text-[#2d2c2c]'
      : 'py-2.5 px-3 cursor-pointer text-sm text-gray-400 hover:text-gray-600 transition-colors';
  };

  const getBadgeClasses = (
    tone: 'accent' | 'neutral' | undefined,
    isActive: boolean
  ) => {
    const base =
      'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 ml-1.5 text-xs font-semibold rounded-full';
    if (tone === 'neutral') {
      // На активной вкладке делаем чуть контрастнее — иначе бейдж
      // теряется на фоне жирного заголовка.
      return isActive
        ? `${base} bg-[#2d2c2c]/10 text-[#2d2c2c]`
        : `${base} bg-gray-100 text-gray-500`;
    }
    return `${base} bg-[#e3282a] text-white`;
  };

  return (
    <div className="w-full">
      <div className='flex justify-between w-full items-center gap-6'>

      <ul className={`flex shrink-0 gap-0 list-none m-0 p-0 ${variant === 'booking' ? 'border-b border-[#2d2c2c]/20' : 'gap-2'}`}>
        {filteredChildren.map((child: any, index) => (
          <li
            key={index}
            className={`${getTabClasses(index)} whitespace-nowrap`}
            onClick={() => setActiveTab(index)}
          >
            {child.props.title}
            {child.props.badge !== undefined && child.props.badge !== null && (
              <span
                className={getBadgeClasses(
                  child.props.badgeTone,
                  activeTab === index
                )}
              >
                {child.props.badge}
              </span>
            )}
          </li>
        ))}
      </ul>
      {contentAfterTabs && (
          contentAfterTabs()
      )}
      </div>

      <div className={`mt-5 ${className}`}>
        {filteredChildren.map((child: any, index) =>
          activeTab === index ? child.props.children : undefined
        )}
      </div>
    </div>
  );
};

export { Tab };
export default Tabs;
